'use client'

import { slugify } from '@/lib/sanitise-strings'

import { useEffect, useRef, useState } from 'react'

import AnchoredContentSidebar from '@/components/anchored-content/AnchoredContentSidebar'
import TeamArchiveItems from '@/components/team-archive/TeamArchiveItems'

export default function TeamArchive({ data }) {
  const [keyword, setKeyword] = useState('')
  const [hasScrolled, setHasScrolled] = useState(false)

  const [sections, setSections] = useState([])
  const [filteredGroups, setFilteredGroups] = useState({})

  const componentRef = useRef(null)
  const sectionRefs = useRef([])

  // Scroll to the anchor when the page loads
  function scrollToAnchor() {
    if (typeof window === 'undefined' || !window.location.hash) {
      return
    }

    const anchorEl = document.querySelector(window.location.hash)

    if (window.location.hash) {
      anchorEl.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      })
    }

    setHasScrolled(true)
  }

  // Filter results based on keyword
  useEffect(() => {
    const filteredGroups = Object.keys(data).reduce((acc, groupName) => {
      const teamMembers = data[groupName].filter((member) => {
        const { title, job_title: jobTitle, keywords, content } = member
        return (
          title.toLowerCase().includes(keyword.toLowerCase()) ||
          jobTitle.toLowerCase().includes(keyword.toLowerCase()) ||
          keywords.toLowerCase().includes(keyword.toLowerCase()) ||
          content.toLowerCase().includes(keyword.toLowerCase())
        )
      })

      if (teamMembers.length > 0) {
        acc[groupName] = teamMembers
      }

      return acc
    }, {})

    const sections = Object.keys(filteredGroups).map((group) => {
      return {
        anchorId: slugify(group),
        title: group,
      }
    })

    setSections(sections)
    setFilteredGroups(filteredGroups)
  }, [data, keyword])

  return (
    <>
      <div className="container--outside border-y border-gradient py-s ms-up:mb-l">
        <section className="container flex justify-between xs:flex-col xs:gap-y-s xs-up:items-center">
          <div className="mx-auto w-full max-w-[var(--container-s-max-w)]">
            <input
              className="ais-SearchBox-input icon-left border shadow-none focus:border-2 focus:!border-gradient"
              aria-label="Search"
              placeholder="Search"
              type="search"
              defaultValue={keyword}
              onChange={(e) => {
                setKeyword(e.target.value)
              }}
            />
          </div>
        </section>
      </div>

      {filteredGroups && Object.keys(filteredGroups).length ? (
        <div ref={componentRef} className="component component--anchored-content ms:pt-0">
          <div className="container @container">
            <div className="@xl-up:border-r flex items-start justify-start gap-m border-gradient gradient-angle-[to_bottom] @l-up:border-l ms:flex-col ms:breakout">
              <div className="sticky z-30 flex-shrink-[1.5] duration-500 ease-in-out ms:top-[var(--header-height)] ms:min-w-full ms:bg-theme ms-up:top-[var(--sticky-offset)] ms-up:w-[419px]">
                <AnchoredContentSidebar
                  sections={sections}
                  componentRef={componentRef}
                  sectionRefs={sectionRefs}
                />
              </div>

              <div className="block w-full @container/team-grid ms:px-[var(--container-padding)]">
                {Object.keys(filteredGroups).map((groupName, index) => {
                  const teamMembers = filteredGroups[groupName]
                  const sectionAnchor = slugify(groupName)

                  return (
                    <div key={index} className={`mb-xxl last:mb-0`}>
                      <div
                        id={sectionAnchor}
                        className="anchor"
                        ref={(el) => {
                          sectionRefs.current[index] = el

                          if (
                            typeof window !== 'undefined' &&
                            window.location.hash === `#${sectionAnchor}` &&
                            !hasScrolled
                          ) {
                            scrollToAnchor()
                          }
                        }}></div>

                      <h2>{groupName}</h2>

                      <TeamArchiveItems items={teamMembers} />
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}
